import React, { useState, useEffect, useMemo } from "react";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import handleService from "../../services/apiService.ts";
import { MethodsEnum } from "../../enums/Methods.ts";
import { useNavigate } from "react-router-dom";
import { Response } from "../../types/Api.ts";
import UpdateInstrumentadorDrawer from "../../components/Modals/UpdateInstrumentadorDrawer/index.tsx";
import CreateInstrumentadorDrawer from "../../components/Modals/CreateInstrumentadorDrawer/index.tsx";
import ConfirmDeleteDialog from "../../components/Modals/ConfirmDeleteDialog/index.tsx";
import { maskCpfParcial } from "../../utils/cpf.util.ts";
import { formatBrazilPhone } from "../../utils/phone.util.ts";
import Button from "../../ui/Button.tsx";
import SearchField from "../../ui/SearchField.tsx";
import EmptyState from "../../ui/EmptyState.tsx";
import SkeletonRows from "../../ui/SkeletonRows.tsx";
import "../../styles/design-system.css";

interface Instrumentador {
  id: string;
  nome: string;
  cpf: string;
  email: string;
  telefone: string;
  notificarEmail: boolean;
}

const ListagemInstrumentador = () => {
  const navigate = useNavigate();
  const [instrumentadores, setInstrumentadores] = useState<Instrumentador[]>([]);
  const [busca, setBusca] = useState("");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [currentInstrumentador, setCurrentInstrumentador] = useState<Instrumentador | null>(null);
  const [instrumentadorParaExcluir, setInstrumentadorParaExcluir] = useState<Instrumentador | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExcluindo, setIsExcluindo] = useState(false);

  useEffect(() => {
    fetchInstrumentadores();
  }, []);

  const fetchInstrumentadores = async () => {
    try {
      setIsLoading(true);
      const response = await handleService({ endpoint: "/instrumentador" });
      const lista: Instrumentador[] = Array.isArray(response) ? response : response.data || [];
      setInstrumentadores(lista);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditar = (instrumentador: Instrumentador) => {
    setCurrentInstrumentador(instrumentador);
    setIsEditOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditOpen(false);
    setCurrentInstrumentador(null);
  };

  const handleCloseView = () => {
    setIsViewOpen(false);
    setCurrentInstrumentador(null);
  };

  const onUpdate = (response: Response<Instrumentador>) => {
    const instrumentador = response.data;
    if (instrumentador) {
      setInstrumentadores((prev) => prev.map((i) => (i.id === instrumentador.id ? instrumentador : i)));
      setIsEditOpen(false);
    }
  };

  const onCreated = (response: Response<Instrumentador>) => {
    const instrumentador = response.data;
    if (instrumentador) {
      setInstrumentadores((prev) => [...prev, instrumentador]);
      setIsCreateOpen(false);
    }
  };

  const handleConfirmarExclusao = async () => {
    if (!instrumentadorParaExcluir) return;
    try {
      setIsExcluindo(true);
      await handleService({ endpoint: `/instrumentador/${instrumentadorParaExcluir.id}`, method: MethodsEnum.DELETE });
      setInstrumentadores((prev) => prev.filter((i) => i.id !== instrumentadorParaExcluir.id));
      setInstrumentadorParaExcluir(null);
    } catch (error) {
    } finally {
      setIsExcluindo(false);
    }
  };

  const filtrados = useMemo(
    () => instrumentadores.filter((i) => i.nome?.toLowerCase().includes(busca.toLowerCase())),
    [instrumentadores, busca]
  );

  return (
    <div className="ds">
      <div className="ds-page-head">
        <div>
          <h1>Instrumentadores</h1>
          <p>Visualize e gerencie os instrumentadores da sua clínica.</p>
        </div>
        <div className="ds-page-head-actions">
          <Button variant="primary" icon={<AddCircleOutlineIcon fontSize="small" />} onClick={() => setIsCreateOpen(true)}>
            Novo instrumentador
          </Button>
        </div>
      </div>

      <div className="ds-toolbar">
        <SearchField value={busca} onChange={setBusca} placeholder="Buscar por nome..." />
      </div>

      <div className="ds-table-wrap">
        <table className="ds-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>CPF</th>
              <th>E-mail</th>
              <th>Telefone</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows columns={5} />
            ) : filtrados.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyState title="Nenhum instrumentador encontrado" description="Cadastre o primeiro instrumentador para começar." actionLabel="Novo instrumentador" onAction={() => setIsCreateOpen(true)} />
                </td>
              </tr>
            ) : (
              filtrados.map((instrumentador) => (
                <tr key={instrumentador.id}>
                  <td className="ds-cell-title">{instrumentador.nome}</td>
                  <td>{maskCpfParcial(instrumentador.cpf)}</td>
                  <td>{instrumentador.email || "—"}</td>
                  <td>{formatBrazilPhone(instrumentador.telefone) || instrumentador.telefone || "—"}</td>
                  <td>
                    <div className="ds-row-actions">
                      <button className="ds-icon-btn" onClick={() => navigate(`/instrumentador/${instrumentador.id}/perfil`)} title="Visualizar">
                        <VisibilityOutlinedIcon fontSize="small" />
                      </button>
                      <button className="ds-icon-btn" onClick={() => handleEditar(instrumentador)} title="Editar">
                        <EditOutlinedIcon fontSize="small" />
                      </button>
                      <button className="ds-icon-btn" onClick={() => setInstrumentadorParaExcluir(instrumentador)} title="Excluir">
                        <DeleteOutlineIcon fontSize="small" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <UpdateInstrumentadorDrawer isOpen={isEditOpen} onClose={handleCloseEdit} instrumentador={currentInstrumentador} onUpdate={onUpdate} />
      <UpdateInstrumentadorDrawer isOpen={isViewOpen} onClose={handleCloseView} instrumentador={currentInstrumentador} readOnly />
      <CreateInstrumentadorDrawer open={isCreateOpen} onClose={() => setIsCreateOpen(false)} onCreated={onCreated} />

      <ConfirmDeleteDialog
        open={!!instrumentadorParaExcluir}
        title="Excluir Instrumentador"
        message="Tem certeza que deseja excluir este instrumentador cadastrado? Esta ação não poderá ser desfeita."
        loading={isExcluindo}
        onCancel={() => setInstrumentadorParaExcluir(null)}
        onConfirm={handleConfirmarExclusao}
      />
    </div>
  );
};

export default ListagemInstrumentador;
